import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { usersAPI, soCreateAPI } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus, User, Check, X, MapPin, MagnifyingGlass,
  PencilSimple, Key, CaretRight, CaretLeft, Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access' },
  { value: 'front_desk', label: 'Front Desk', desc: 'Create transactions for assigned branches' },
  { value: 'accountant', label: 'Accountant', desc: 'Financial access' },
  { value: 'manager', label: 'Manager', desc: 'Manage assigned branches' },
  { value: 'admin', label: 'Admin', desc: 'Full access to all branches' },
];
const roleLabel = (r) => ROLES.find(x => x.value === r)?.label || r;
const roleBadge = (r) => ({
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-amber-100 text-amber-700',
  accountant: 'bg-blue-100 text-blue-700',
  front_desk: 'bg-teal-100 text-teal-700',
}[r] || 'bg-gray-100 text-gray-700');

const emptyForm = { username: '', password: '', full_name: '', email: '', role: 'viewer', allowed_locations: [], default_location: '' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [locSearch, setLocSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [pwDialog, setPwDialog] = useState(null); // {id, name, pw}

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersAPI.list();
      setUsers(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const { data } = await soCreateAPI.branches();
      setBranches(data.branches || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadUsers(); loadBranches(); }, [loadUsers, loadBranches]);

  const branchName = (id) => branches.find(b => b.id === id)?.name || id;

  const openAdd = () => { setEditingUser(null); setForm(emptyForm); setLocSearch(''); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      username: u.username, password: '', full_name: u.full_name, email: u.email,
      role: u.role, allowed_locations: u.allowed_locations || [], default_location: u.default_location || '',
    });
    setLocSearch('');
    setDialogOpen(true);
  };

  const addLocation = (id) => setForm(f => ({
    ...f,
    allowed_locations: f.allowed_locations.includes(id) ? f.allowed_locations : [...f.allowed_locations, id],
    default_location: f.default_location || id,
  }));
  const removeLocation = (id) => setForm(f => ({
    ...f,
    allowed_locations: f.allowed_locations.filter(x => x !== id),
    default_location: f.default_location === id ? (f.allowed_locations.filter(x => x !== id)[0] || '') : f.default_location,
  }));
  const selectAll = () => setForm(f => ({
    ...f,
    allowed_locations: branches.map(b => b.id),
    default_location: f.default_location || branches[0]?.id || '',
  }));
  const removeAll = () => setForm(f => ({ ...f, allowed_locations: [], default_location: '' }));

  const handleSave = async () => {
    if (!form.full_name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!editingUser && (!form.username.trim() || !form.password.trim())) { toast.error('Username and password are required'); return; }
    setSaving(true);
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, {
          full_name: form.full_name, email: form.email, role: form.role,
          allowed_locations: form.allowed_locations, default_location: form.default_location,
        });
        toast.success('User updated');
      } else {
        await usersAPI.create(form);
        toast.success('User created');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save user');
    } finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    try {
      await usersAPI.toggleActive(u.id);
      toast.success('Status updated');
      loadUsers();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update status');
    }
  };

  const handleResetPw = async () => {
    if (!pwDialog.pw || pwDialog.pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await usersAPI.resetPassword(pwDialog.id, pwDialog.pw);
      toast.success(`Password reset for ${pwDialog.name}`);
      setPwDialog(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to reset password');
    }
  };

  const available = branches.filter(b =>
    !form.allowed_locations.includes(b.id) &&
    (!locSearch || b.name.toLowerCase().includes(locSearch.toLowerCase()) || (b.state_code || '').toLowerCase().includes(locSearch.toLowerCase()))
  );
  const selected = branches.filter(b => form.allowed_locations.includes(b.id));

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Add users, assign roles and configure branch access"
        actions={
          <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="add-user-btn" onClick={openAdd}>
            <Plus size={16} className="mr-2" /> Add User
          </Button>
        }
      />

      <div className="page-content">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th><th>Username</th><th>Role</th><th>Branch Access</th>
                <th>Status</th><th>Created</th><th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>{[...Array(7)].map((_, j) => <td key={j}><div className="skeleton h-4 w-20 rounded" /></td>)}</tr>
                ))
              ) : users.length > 0 ? users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center"><User size={16} className="text-[#64748B]" /></div>
                      <div>
                        <div className="font-medium">{u.full_name}</div>
                        <div className="text-xs text-[#64748B]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-sm">{u.username}</td>
                  <td><span className={`text-xs px-2 py-1 rounded-full ${roleBadge(u.role)}`}>{roleLabel(u.role)}</span></td>
                  <td>
                    {u.role === 'admin' ? (
                      <span className="text-xs text-[#64748B]">All branches</span>
                    ) : u.allowed_locations?.length > 0 ? (
                      <span className="text-xs text-[#0F4C81]" title={u.allowed_locations.map(branchName).join(', ')}>
                        {u.allowed_locations.length} branch{u.allowed_locations.length > 1 ? 'es' : ''}
                        {u.default_location ? ` · default: ${branchName(u.default_location)}` : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-[#64748B]">All branches (unrestricted)</span>
                    )}
                  </td>
                  <td>
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-sm"><Check size={14} weight="bold" />Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#DC2626] text-sm"><X size={14} weight="bold" />Inactive</span>
                    )}
                  </td>
                  <td className="text-[#64748B] text-sm">{formatDateTime(u.created_at)}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}><PencilSimple size={14} className="mr-1" />Edit</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPwDialog({ id: u.id, name: u.full_name, pw: '' })} data-testid={`reset-pw-${u.username}`}><Key size={14} className="mr-1" />Reset</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleToggle(u)} data-testid={`toggle-user-${u.username}`}>{u.is_active ? 'Deactivate' : 'Activate'}</Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit user dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">{editingUser ? `Edit User — ${editingUser.full_name}` : 'Add New User'}</DialogTitle>
            <DialogDescription>Set the user's details, role and the branches they can access.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Full Name</label>
              <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="John Doe" data-testid="user-fullname" />
            </div>
            <div>
              <label className="form-label">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="john@hearclear.com" data-testid="user-email" />
            </div>
            <div>
              <label className="form-label">Username</label>
              <Input value={form.username} disabled={!!editingUser} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="johndoe" data-testid="user-username" />
            </div>
            {!editingUser && (
              <div>
                <label className="form-label">Password</label>
                <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" data-testid="user-password" />
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="form-label">Role</label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <span className="font-medium">{r.label}</span> <span className="text-xs text-gray-500">— {r.desc}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Configure Location Access (Zoho-style two-panel) */}
          <div className="mt-4 border-t border-[#E2E8F0] pt-4">
            <h4 className="text-sm font-semibold text-[#0F172A] mb-1">Configure Branch Access</h4>
            <p className="text-xs text-[#64748B] mb-3">
              {form.role === 'admin'
                ? 'Admins have access to all branches — no selection needed.'
                : 'Select the branches this user can create and access transactions for. Leave empty for access to all branches.'}
            </p>

            {form.role !== 'admin' && (
              <div className="grid grid-cols-2 gap-3">
                {/* Available */}
                <div className="border border-[#E2E8F0] rounded-lg flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <span className="text-xs font-medium text-[#64748B]">Available Branches</span>
                    <button className="text-xs text-[#0F4C81] font-medium" onClick={selectAll} data-testid="loc-select-all">Select All</button>
                  </div>
                  <div className="p-2">
                    <div className="relative mb-2">
                      <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input value={locSearch} onChange={e => setLocSearch(e.target.value)} placeholder="Search..." className="pl-7 h-8 text-sm" data-testid="loc-search" />
                    </div>
                    <div className="max-h-[220px] overflow-y-auto space-y-1">
                      {available.length ? available.map(b => (
                        <button key={b.id} onClick={() => addLocation(b.id)} data-testid={`loc-available-${b.id}`}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#EBF3FA] text-left group">
                          <MapPin size={13} className="text-[#0F4C81] flex-shrink-0" />
                          <span className="text-sm text-[#0F172A] flex-1 truncate">{b.name} {b.state_code ? <span className="text-xs text-gray-400">({b.state_code})</span> : ''}</span>
                          <CaretRight size={13} className="text-gray-300 group-hover:text-[#0F4C81]" />
                        </button>
                      )) : <p className="text-xs text-gray-400 px-2 py-3 text-center">No branches</p>}
                    </div>
                  </div>
                </div>

                {/* Accessible / selected */}
                <div className="border border-[#E2E8F0] rounded-lg flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0] bg-[#EBF3FA]">
                    <span className="text-xs font-medium text-[#0F4C81]">Accessible Branches ({selected.length})</span>
                    <button className="text-xs text-[#DC2626] font-medium" onClick={removeAll} data-testid="loc-remove-all">Remove All</button>
                  </div>
                  <div className="p-2 max-h-[262px] overflow-y-auto space-y-1">
                    {selected.length ? selected.map(b => (
                      <div key={b.id} data-testid={`loc-selected-${b.id}`}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded bg-[#EBF3FA]">
                        <CaretLeft size={13} className="text-gray-300 cursor-pointer hover:text-[#DC2626]" onClick={() => removeLocation(b.id)} />
                        <button onClick={() => setForm(f => ({ ...f, default_location: b.id }))} title="Set as default"
                          data-testid={`loc-default-${b.id}`}>
                          <Star size={13} weight={form.default_location === b.id ? 'fill' : 'regular'} className={form.default_location === b.id ? 'text-amber-500' : 'text-gray-300'} />
                        </button>
                        <span className="text-sm text-[#0F172A] flex-1 truncate">{b.name}</span>
                        <X size={13} className="text-gray-400 cursor-pointer hover:text-[#DC2626]" onClick={() => removeLocation(b.id)} />
                      </div>
                    )) : <p className="text-xs text-gray-400 px-2 py-6 text-center">No branches selected → user sees all</p>}
                  </div>
                </div>
              </div>
            )}

            {form.role !== 'admin' && selected.length > 0 && (
              <div className="mt-3 max-w-xs">
                <label className="form-label">Default Branch</label>
                <Select value={form.default_location} onValueChange={v => setForm(f => ({ ...f, default_location: v }))}>
                  <SelectTrigger data-testid="default-location"><SelectValue placeholder="Select default branch" /></SelectTrigger>
                  <SelectContent>
                    {selected.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? 'Saving...' : editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwDialog} onOpenChange={(o) => { if (!o) setPwDialog(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for this user.</DialogDescription></DialogHeader>
          <p className="text-sm text-[#64748B]">Set a new password for <strong>{pwDialog?.name}</strong>.</p>
          <Input type="password" placeholder="New password (min 6 chars)" value={pwDialog?.pw || ''}
            onChange={e => setPwDialog(p => ({ ...p, pw: e.target.value }))} data-testid="reset-pw-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)}>Cancel</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleResetPw} data-testid="reset-pw-submit">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
